import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FORMATS, formatLabel } from "@/lib/campus";
import { toast } from "sonner";
import { ProfessorSays } from "@/components/campus/Professor";
import { PlusCircle, DollarSign, Sparkles, Award, Users, Star, Repeat, Target, Bot } from "lucide-react";

const FILTERS = [{ value: "all", label: "Everything" }, ...FORMATS.map((f) => ({ value: f.value, label: f.label }))];

const LEVEL_BG = { Beginner: "bg-emerald-500/80", Intermediate: "bg-sky-500/80", Advanced: "bg-violet-600/80" };

const CourseCard = ({ course, enrolled, onOpen }) => (
  <button type="button" data-testid={`course-card-${course.course_id}`} onClick={onOpen}
    className="text-left bg-[#121721] border border-white/10 hover:border-amber-500/50 rounded-2xl overflow-hidden flex flex-col justify-between transition-[transform,border-color] duration-200 hover:-translate-y-1 group">
    {course.cover && (
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={course.cover} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {course.level && <span className={`absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur-md text-white ${LEVEL_BG[course.level] || "bg-violet-600/80"}`}>{course.level}{course.course_id === "platform-ai-intro" ? " · Zero experience" : ""}</span>}
        {course.ai_taught && <span data-testid={`ai-taught-badge-${course.course_id}`} className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#0B0E14]/80 text-amber-300 border border-amber-500/40 backdrop-blur-md"><Bot className="h-3 w-3" /> AI-taught</span>}
      </div>
    )}
    <div className="p-6 pb-0">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">{formatLabel(course.format)}</span>
        <span className="text-[11px] text-slate-500">{course.category}</span>
        {course.marketable_skill && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><Target className="h-3 w-3" />Marketable</span>}
        {course.ongoing && <span className="text-[11px] text-amber-300 flex items-center gap-1"><Repeat className="h-3 w-3" />Ongoing</span>}
      </div>
      <h3 className="text-lg font-bold text-white leading-snug group-hover:text-amber-300 transition-colors">{course.title}</h3>
      <p className="text-slate-400 text-sm mt-2 line-clamp-2">{course.description}</p>
    </div>
    <div className="flex items-end justify-between mt-6 mx-6 mb-6 pt-4 border-t border-white/5">
      <div className="text-xs text-slate-500">
        <div className="text-slate-300 font-medium">{course.instructor_name}</div>
        <div className="flex items-center gap-1 mt-0.5"><Star className="h-3 w-3 text-amber-400" />{course.rating} · {course.students} students · {course.lesson_count} {course.ongoing ? "eps" : "lessons"}</div>
      </div>
      <div className="text-right">
        <div className="text-xl font-extrabold text-amber-400">{course.free ? "Free" : `$${Number(course.price).toFixed(2)}`}</div>
        {enrolled && <div className="text-[11px] text-emerald-400">Enrolled</div>}
      </div>
    </div>
  </button>
);

export default function HustlersCampus() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)).catch(() => toast.error("Couldn't load courses")); }, []);
  useEffect(() => {
    if (!user) { setEnrolled([]); return; }
    api.get("/my-enrollments").then(({ data }) => setEnrolled(data)).catch(() => {});
  }, [user]);

  const teach = () => (user ? navigate("/teach") : (toast.error("Sign in to teach"), navigate("/login")));
  const shown = courses
    .filter((c) => filter === "all" || (c.format || "course") === filter)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || Number(!!b.marketable_skill) - Number(!!a.marketable_skill));

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 space-y-12">
        <header className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <div>
            <div className="flex items-center gap-2 text-amber-400 font-semibold text-xs uppercase tracking-[0.2em] mb-4"><Sparkles className="w-4 h-4" /> Hustler's Campus</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">Share what you know.<br /><span className="text-amber-400">Build wealth together.</span></h1>
            <p className="text-slate-400 mt-5 max-w-xl text-sm md:text-base">From a 5-minute lecture to a months-long course. Upload video, audio or slides, set a price from $2, and keep 93% of every enrollment.</p>
            <div className="mt-6 max-w-xl border-l-2 border-amber-500 pl-4" data-testid="campus-anyone-can-teach">
              <div className="text-white font-bold text-lg">Anyone can teach.</div>
              <p className="text-slate-300 text-sm md:text-base mt-1">If you have a passion, a special skill, or possess a vast knowledge on a particular subject, become an instructor for a chance at earning residual income.</p>
              <p className="text-amber-300 text-sm font-semibold italic mt-2">There is no such thing as useless knowledge.</p>
            </div>
            <div className="mt-4 max-w-xl flex gap-3 items-start rounded-xl bg-white/[0.03] border border-white/10 p-4" data-testid="campus-ai-policy">
              <Bot className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300"><strong className="text-white">Only classes about AI are taught by AI.</strong> Every other class on Campus is taught by a real, verified human instructor, and courses estimated to be more than 45% AI-written are turned away.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              <button data-testid="campus-teach-btn" onClick={teach} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3.5 rounded-full transition-transform hover:-translate-y-0.5">
                <PlusCircle className="w-5 h-5" /> Start teaching
              </button>
              <a href="#courses" data-testid="campus-browse-btn" className="px-6 py-3.5 rounded-full border border-white/15 hover:border-amber-500/60 text-white font-semibold">Browse courses</a>
            </div>
          </div>
          <div className="grid gap-3">
            {[
              { icon: DollarSign, title: "From $2, your price", text: "Quick lecture or full course — you decide." },
              { icon: Users, title: "Creators keep 93%", text: "Flat 7% fee. Popular courses become residual income." },
              { icon: Award, title: "AI-reviewed for value", text: "Real teaching only. Marketable skills get boosted." },
            ].map((f) => (
              <div key={f.title} className="bg-[#121721] border border-white/10 rounded-xl p-4 flex items-start gap-3">
                <div className="p-2.5 bg-amber-500/10 text-amber-400 rounded-lg"><f.icon className="w-5 h-5" /></div>
                <div><h4 className="font-semibold text-white text-sm">{f.title}</h4><p className="text-xs text-slate-400 mt-0.5">{f.text}</p></div>
              </div>
            ))}
          </div>
        </header>

        <section className="max-w-3xl" data-testid="campus-professor-intro">
          <ProfessorSays size={84} text="Hello! I'm Professor Sparks, your lecturer for the official Campus courses. I only teach classes about AI — everything else here is taught by real people. New to AI? Start with 'Introduction to AI', no experience needed." />
        </section>

        <section id="courses">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-2xl font-bold text-white">Explore</h2>
            <div className="flex flex-wrap gap-2" data-testid="campus-filters">
              {FILTERS.map((f) => (
                <button key={f.value} data-testid={`campus-filter-${f.value}`} onClick={() => setFilter(f.value)}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${filter === f.value ? "bg-amber-500 text-slate-950 border-amber-500 font-bold" : "border-white/10 text-slate-300 hover:border-amber-500/50"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {shown.map((c) => <CourseCard key={c.course_id} course={c} enrolled={enrolled.includes(c.course_id)} onOpen={() => navigate(`/campus/${c.course_id}`)} />)}
          </div>
          {shown.length === 0 && <div className="text-center text-slate-500 py-16" data-testid="campus-empty">Nothing here yet — be the first to teach one.</div>}
        </section>
      </div>
      <Footer />
    </div>
  );
}
